<template lang="pug">
.content-inner.my-pulse-config
  .total-metrics-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.all_segments_metrics') }}
      .metric-count {{ config.total_metrics.length }} / {{ maxMetrics }}
    PulseMetricPicker(
      :metrics="config.total_metrics"
      :availableMetrics="availableMetrics"
      :maxMetrics="maxMetrics"
      :disabled="disabled"
      size="small"
      @update="onTotalMetricsUpdate"
    )

  PulseConfigSegment(
    v-for="(segment, index) in config.segments"
    :key="index"
    :segment="segment"
    :index="index"
    :availableSegments="availableSegmentsByIndex[index]"
    :availableMetrics="availableMetrics"
    :maxMetrics="maxMetrics"
    :disabled="disabled"
    size="small"
    @update="onSegmentUpdate"
  )

  .no-interaction-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.no_interaction.title') }}
        aiq-tooltip(placement="top" :content="$t('settings_tab.agent_tab.dashboard.pulse_configuration.no_interaction.tooltip')")
          fa-icon.section-info(:icon="['fas', 'circle-question']")
      aiq-switch(
        active-text=""
        inactive-text=""
        :model-value="config.include_no_interaction"
        :disabled="disabled"
        @update:model-value="onIncludeNoInteractionChange"
      )

  .sales-lead-tags-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.title') }}
        aiq-tooltip(placement="top" :content="$t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.tooltip')")
          fa-icon.section-info(:icon="['fas', 'circle-question']")
    aiq-select(
      :model-value="config.sales_lead_tags"
      multiple
      filterable
      size="small"
      :disabled="disabled"
      :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.placeholder')"
      popper-class="pulse-config-dropdown"
      @update:model-value="onSalesLeadTagsChange"
    )
      aiq-option(
        v-for="tagName in availableTags"
        :key="tagName"
        :label="tagName"
        :value="tagName"
      )
</template>

<script>
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import difference from 'lodash/difference';
import times from 'lodash/times';
import { useVuelidate } from '@vuelidate/core';
import PulseConfigSegment from '@/pages/Settings/Agents/children/Dashboard/components/PulseConfigSegment.vue';
import PulseMetricPicker from '@/pages/Settings/Agents/children/Dashboard/components/PulseMetricPicker.vue';
import { METRIC_IDS, MAX_METRICS_PER_SECTION, MAX_SEGMENT_SLOTS } from '@/pages/Pulse/365/constants';
import { TAG_LIMIT } from '@/constants/pagination';

const emptyConfig = () => ({
  total_metrics: [],
  segments: times(MAX_SEGMENT_SLOTS, () => ({ segment_names: [], metrics: [] })),
  sales_lead_tags: [],
  include_no_interaction: true,
});

export default {
  name: 'MyPulse365Config',
  components: {
    PulseConfigSegment,
    PulseMetricPicker,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      record: {
        preferences: {
          pulse_configuration: { $autoDirty: true },
        },
      },
    };
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxMetrics: MAX_METRICS_PER_SECTION,
    };
  },
  computed: {
    ...mapGetters({
      segments: 'configs/segmentNames',
      tags: 'tags/tags',
    }),
    config() {
      const saved = get(this.record, 'preferences.pulse_configuration');
      if (!saved) {
        return emptyConfig();
      }
      const segments = get(saved, 'segments', []);
      return {
        total_metrics: get(saved, 'total_metrics', []),
        segments: times(MAX_SEGMENT_SLOTS, i => ({
          segment_names: get(segments, `${i}.segment_names`, []),
          metrics: get(segments, `${i}.metrics`, []),
        })),
        sales_lead_tags: get(saved, 'sales_lead_tags', []),
        // Anything but an explicit false reads as on, so a configuration saved
        // before the toggle existed shows the state commit would write back.
        include_no_interaction: get(saved, 'include_no_interaction') !== false,
      };
    },
    availableTags() {
      return (this.tags || [])
        .filter(tag => !tag.archived)
        .map(tag => tag.name)
        .sort((a, b) => a.localeCompare(b));
    },
    availableMetrics() {
      return METRIC_IDS
        .map(id => ({
          id,
          label: this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metrics.${id}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    availableSegmentsByIndex() {
      return this.config.segments.map((segment, index) => {
        const otherIndex = index === 0 ? 1 : 0;
        const otherSelected = get(this.config.segments, `${otherIndex}.segment_names`, []);
        const currentSelected = segment.segment_names;
        return difference(this.segments, difference(otherSelected, currentSelected))
          .sort((a, b) => a.localeCompare(b));
      });
    },
  },
  async mounted() {
    if (isEmpty(this.tags)) {
      await this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]);
    }
  },
  methods: {
    commit(next) {
      const normalized = {
        total_metrics: [...get(next, 'total_metrics', [])],
        segments: get(next, 'segments', []).map(segment => ({
          segment_names: [...get(segment, 'segment_names', [])],
          metrics: [...get(segment, 'metrics', [])],
        })),
        sales_lead_tags: [...get(next, 'sales_lead_tags', [])],
        include_no_interaction: get(next, 'include_no_interaction') !== false,
      };
      set(this.record, 'preferences.pulse_configuration', normalized);
    },
    onTotalMetricsUpdate(metrics) {
      this.commit({ ...this.config, total_metrics: metrics });
    },
    onSegmentUpdate(index, updatedSegment) {
      const segments = this.config.segments.map(
        (segment, i) => (i === index ? { ...updatedSegment } : segment),
      );
      this.commit({ ...this.config, segments });
    },
    onSalesLeadTagsChange(tagNames) {
      this.commit({ ...this.config, sales_lead_tags: tagNames || [] });
    },
    onIncludeNoInteractionChange(include) {
      this.commit({ ...this.config, include_no_interaction: include });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/aiq-variables.scss";

.my-pulse-config {
  padding-bottom: 10px;

  // PulseConfigSegment is shared with Settings > Dashboard, where the heavier
  // Settings typography is correct. Override it here instead of at the source
  // so only the modal changes.
  :deep(.segment-row) {
    .title-with-info h3 {
      font-size: 14px;
      font-weight: 100;
      color: #475669;
    }

    // Placeholder text sat at 14px/400 beside a 14px/100 heading, so it read as
    // the heavier of the two.
    .no-segment {
      font-size: 13px;
    }
  }
}

.total-metrics-section {
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

// The last segment row drops its own border, so this section carries the rule
// that separates it from the tags below.
.no-interaction-section {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;

  // aiq-switch reads these off any ancestor, and the library default is blue.
  // Green matches the switches on the Preferences tab beside this one.
  --aiq-switch-on-color: #30C28B;
  --aiq-switch-off-color: #C0CCDA;

  .section-header {
    margin-bottom: 0;
  }
}

.sales-lead-tags-section {
  padding-top: 20px;

  :deep(.el-select) {
    width: 100%;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

// Headings follow the modal's field-label convention (ModalEditor.vue), so this
// tab reads like Profile / Membership / Preferences rather than a Settings page.
.title-with-info {
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 100;
    color: #475669;
  }

  .section-info {
    font-size: 12px;
    color: $aiq-color-text-muted;
    cursor: pointer;
  }
}

.metric-count {
  font-size: 13px;
  color: $aiq-color-text-muted;
  font-weight: 500;
}

</style>

<style lang="scss">
@import "../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .my-pulse-config {
  .title-with-info h3 {
    color: $aiq-color-black;
  }

  .metric-count,
  .title-with-info .section-info {
    color: white;
  }
}
</style>
